import { useEffect } from 'react';
import { Modal, Form, Select, Typography, App, theme } from 'antd';
import { PartitionOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useAssignUserToTenantMutation } from '@/api/auth/mutations';
import { COMPANY_ROLES, TENANT_ROLES, type TenantRoleType } from '@/constants/tenantRoles';
import { useTenantRoleOptions } from '@/hooks/useTenantRoleOptions';
import { useAllTenantsWithCompanies } from '@/hooks/useAllTenantsWithCompanies';
import { ModalTitle } from '@/components/ui/ModalTitle';
import { getApiErrorMessage } from '@/utils/apiError';
import { TenantSelect } from './TenantSelect';
import { CompanySelect } from './CompanySelect';
import { formModalWidth } from '@/components/ui/formModalStyles';
import { FormModalFooter } from '@/components/ui/FormModalFooter';

const { Text } = Typography;

interface AssignTenantFormValues {
  clientId: string;
  tenantRole: TenantRoleType;
  companyIds?: string[];
  companyRole?: TenantRoleType;
}

export interface AssignTenantModalProps {
  open: boolean;
  onCancel: () => void;
  userId: string;
  userName: string;
  /** Tenants the user already belongs to (hidden from the picker) */
  excludeTenantIds: string[];
}

/**
 * Assigns an existing user to a tenant with a tenant role and, for staff roles, optionally to
 * some of the tenant's companies with a company role.
 */
export function AssignTenantModal({ open, onCancel, userId, userName, excludeTenantIds }: AssignTenantModalProps) {
  const { t } = useTranslation('admin');
  const { token } = theme.useToken();
  const { message } = App.useApp();
  const [form] = Form.useForm<AssignTenantFormValues>();
  const assignMutation = useAssignUserToTenantMutation();
  const tenantRoleOptions = useTenantRoleOptions();
  const companyRoleOptions = useTenantRoleOptions(COMPANY_ROLES);
  const { tenantsById } = useAllTenantsWithCompanies(open);

  const clientId = Form.useWatch('clientId', form);
  const tenantRole = Form.useWatch('tenantRole', form);
  const companyIds = Form.useWatch('companyIds', form) ?? [];
  const isCustomer = tenantRole === TENANT_ROLES.CUSTOMER;

  useEffect(() => {
    if (open) {
      form.resetFields();
    }
  }, [open, form]);

  /** Keeps the company role in step with the tenant role (owners usually own their companies too). */
  const handleTenantRoleChange = (role: TenantRoleType) => {
    if (role === TENANT_ROLES.CUSTOMER) {
      form.setFieldsValue({ companyIds: [], companyRole: undefined });
    } else {
      form.setFieldValue('companyRole', role);
    }
  };

  const handleSubmit = async () => {
    let values: AssignTenantFormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    const tenantName = tenantsById.get(values.clientId)?.name ?? '';
    const selectedCompanyIds = isCustomer ? [] : values.companyIds ?? [];
    try {
      const { failedCompanyIds } = await assignMutation.mutateAsync({
        userId,
        clientId: values.clientId,
        tenantRole: values.tenantRole,
        companyIds: selectedCompanyIds,
        companyRole: selectedCompanyIds.length > 0 ? values.companyRole : undefined
      });

      if (failedCompanyIds.length > 0) {
        const companies = tenantsById.get(values.clientId)?.companies ?? [];
        const names = failedCompanyIds.map((id) => companies.find((company) => company.id === id)?.name ?? id).join(', ');
        message.warning(t('users.detail.tenantAccess.assignPartial', { tenant: tenantName, companies: names }));
      } else {
        message.success(t('users.detail.tenantAccess.assigned', { tenant: tenantName }));
      }
      onCancel();
    } catch (error) {
      message.error(getApiErrorMessage(error, t('users.detail.tenantAccess.assignError')));
    }
  };

  return (
    <Modal
      title={
        <ModalTitle icon={<PartitionOutlined />} title={t('users.detail.tenantAccess.assignTitle')} subtitle={userName} />
      }
      open={open}
      footer={null}
      onCancel={onCancel}
      destroyOnHidden
      width={formModalWidth(520)}
    >
      <Text type="secondary" style={{ display: 'block', marginBottom: token.marginMD }}>
        {t('users.detail.tenantAccess.assignDescription', { name: userName })}
      </Text>
      <Form
        form={form}
        layout="vertical"
        initialValues={{ tenantRole: TENANT_ROLES.EMPLOYEE, companyRole: TENANT_ROLES.EMPLOYEE, companyIds: [] }}
      >
        <Form.Item
          name="clientId"
          label={t('users.detail.tenantAccess.tenant')}
          rules={[{ required: true, message: t('users.detail.tenantAccess.tenantRequired') }]}
        >
          <TenantSelect excludeIds={excludeTenantIds} onChange={() => form.setFieldValue('companyIds', [])} />
        </Form.Item>

        <Form.Item
          name="tenantRole"
          label={t('users.detail.tenantAccess.tenantRole')}
          rules={[{ required: true }]}
          extra={tenantRoleOptions.find((option) => option.value === tenantRole)?.description}
        >
          <Select options={tenantRoleOptions.map(({ value, label }) => ({ value, label }))} onChange={handleTenantRoleChange} />
        </Form.Item>

        {!isCustomer && (
          <Form.Item
            name="companyIds"
            label={t('users.detail.tenantAccess.companies')}
            extra={t('users.detail.tenantAccess.companiesHint')}
          >
            <CompanySelect mode="multiple" allowClear clientId={clientId} />
          </Form.Item>
        )}

        {!isCustomer && companyIds.length > 0 && (
          <Form.Item name="companyRole" label={t('users.detail.tenantAccess.companyRole')} rules={[{ required: true }]}>
            <Select options={companyRoleOptions.map(({ value, label }) => ({ value, label }))} />
          </Form.Item>
        )}
      </Form>
      <FormModalFooter
        onCancel={onCancel}
        onSubmit={handleSubmit}
        submitLabel={t('users.detail.tenantAccess.assignSubmit')}
        loading={assignMutation.isPending}
      />
    </Modal>
  );
}
